import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function TooltipProvider({
  delayDuration = 300,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

// Tooltips close on their own, so a pointer parked on a control does not
// leave a label hanging. The time depends on how much there is to read:
// a reaction allowance plus ~70 ms per character (about 14 characters per
// second, a comfortable reading pace for short labels), never under 2 s.
// TooltipContent measures its own text on mount and reports the length
// through context. Controlled internally; an explicit `open` prop wins.
const MIN_HIDE_MS = 2000
const BASE_MS = 800
const PER_CHAR_MS = 70

export function autoHideMs(chars: number): number {
  return Math.max(MIN_HIDE_MS, BASE_MS + chars * PER_CHAR_MS)
}

const HideTimerContext = React.createContext<(chars: number) => void>(() => {})

function Tooltip({
  open: openProp,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [open, setOpen] = React.useState(false)
  const timer = React.useRef<number | null>(null)

  const clear = () => {
    if (timer.current != null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }

  const close = React.useCallback(() => {
    timer.current = null
    setOpen(false)
    onOpenChange?.(false)
  }, [onOpenChange])

  const arm = React.useCallback(
    (ms: number) => {
      clear()
      timer.current = window.setTimeout(close, ms)
    },
    [close],
  )

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    onOpenChange?.(next)
    clear()
    if (next) arm(MIN_HIDE_MS) // content re-arms with its measured length
  }

  const report = React.useCallback((chars: number) => arm(autoHideMs(chars)), [arm])

  React.useEffect(() => clear, [])

  return (
    <HideTimerContext.Provider value={report}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={openProp ?? open}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </HideTimerContext.Provider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

// Styled as a small card in the interface palette (surface fill, border,
// 6 px radius, body font) rather than shadcn's inverted default; no arrow.
function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const report = React.useContext(HideTimerContext)
  const ref = React.useRef<HTMLDivElement>(null)

  // Mounted only while open: measure the rendered text and re-arm the timer.
  React.useEffect(() => {
    report(ref.current?.textContent?.trim().length ?? 0)
  }, [report])

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md border border-line-strong bg-card px-2.5 py-1.5 font-sans text-xs text-card-foreground shadow-[0_4px_16px_rgba(0,0,0,0.25)]",
          "animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
